/** @module utils/api */

/**
 * API_ERROR_MESSAGES is a constant object that defines common error messages for API responses.
 */
export const API_ERROR_MESSAGES: Record<string, string> = {
    UNAUTHORIZED: 'Unauthorized',
    FORBIDDEN: 'Forbidden',
    NOT_FOUND: 'Not Found',
    INTERNAL_SERVER_ERROR: 'Internal Server Error',
    BAD_GATEWAY: 'Bad Gateway',
    SERVICE_UNAVAILABLE: 'Service Unavailable',
    GATEWAY_TIMEOUT: 'Gateway Timeout',
    ABORTED: 'Aborted',
    CANCELED: 'canceled',
};

/**
 * Data structure for API error responses.
 */
interface ApiErrorResponse {
    data?: {
        data?: {
            errors?: {messages: string[]}[];
        };
    };
}

/**
 * Catches and formats errors from the API.
 *
 * @param data - The error response received from the API.
 * @returns Throws an error with the extracted error messages.
 */
export const catchApiResponse = (data: ApiErrorResponse): never => {
    throw new Error(
        data.data.data.errors
            .map((error) => error.messages.join('. '))
            .join('. ')
    );
};

/**
 * Metadata for pagination.
 */
interface PaginationMeta {
    next?: string | null;
    previous?: string | null;
    count?: number;
}

/**
 * Parsed pagination metadata.
 */
export interface ParsedPaginationMeta {
    page: number;
    perPage: number;
    pages: number;
    prevPage: number | null;
    nextPage: number | null;
    total: number;
}

/**
 * Parses a given string into a URL object safely.
 *
 * This function attempts to parse a provided URL string into a URL object. If the input is null,
 * undefined, or an invalid URL string, it returns null instead of throwing an error.
 *
 * @param urlString - The URL string to parse. Can be a string, null, or undefined.
 * @returns A URL object if the input is a valid URL string, or null if the input is invalid, null, or undefined.
 */
const safelyParseURL = (urlString: string | null | undefined): URL | null => {
    try {
        return urlString ? new URL(urlString) : null;
    } catch {
        return null;
    }
};

/**
 * Parses link-based pagination metadata and converts to page-based pagination meta.
 *
 * @param meta - The pagination metadata object.
 * @returns The formatted pagination parameters.
 *
 * @example
 * const meta = {
 *    next: 'https://api.spothero.com/v1/blackouts?page=2&perPage=25',
 *    previous: null,
 *    count: 100,
 * };
 *
 * const formattedMeta = parsePaginationMeta(meta);
 * console.log(formattedMeta);
 * {
 *    page: 1,
 *    perPage: 25,
 *    pages: 4,
 *    prevPage: null,
 *    nextPage: 2,
 *    total: 100,
 * }
 */
export const parsePaginationMeta = (
    meta: PaginationMeta
): ParsedPaginationMeta => {
    const {next, previous, count} = meta;
    const nextUrl = safelyParseURL(next);
    const prevUrl = safelyParseURL(previous);
    const nextParams = new URLSearchParams(nextUrl?.search || '');
    const prevParams = new URLSearchParams(prevUrl?.search || '');

    const nextPage = nextParams.has('page')
        ? Number(nextParams.get('page'))
        : null;
    const prevPage = prevParams.has('page')
        ? Number(prevParams.get('page'))
        : null;

    const perPage =
        Number(nextParams.get('perPage')) ||
        Number(prevParams.get('perPage')) ||
        count;

    const formattedParams = {
        page:
            nextPage !== null
                ? nextPage - 1
                : prevPage !== null
                  ? prevPage + 1
                  : 1,
        perPage,
        pages: Math.ceil(count / perPage),
        prevPage,
        nextPage,
        total: count,
    };

    return formattedParams;
};
